<script setup lang="ts">
import type { AUTH_PAGE_ORIGIN as AUTH_PAGE_ORIGIN_ENUM } from '@/enums/auth';
import type { AuthenticationType, RedirectIds } from '@/types/auth';

enum AUTH_PAGE {
	LOGIN,
	VERIFICATION,
	CONNECT_WITH_SNS,
}

defineProps({
	isOpen: {
		type: Boolean,
		required: true,
		default: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	eventId: {
		type: Number as PropType<number | null>,
		default: null,
	},
	redirectIds: {
		type: Object as PropType<RedirectIds>,
		default: () => {},
	},
	page: {
		type: String as PropType<AUTH_PAGE_ORIGIN_ENUM>,
		default: '',
	},
});

const emit = defineEmits<{
	(e: 'onClose'): void
	(e: 'onCancel'): void
	(e: 'onSuccess', isLoggedIn: boolean): void
	(e: 'onGoogleLoginFailed'): void
}>();

const authStore = useAuthStore();

const step = ref(AUTH_PAGE.LOGIN);
const formType = ref<AuthenticationType>(AUTH_FORM.SIGN_IN);

const sidebarHeaderMsg = computed(() => {
	if (step.value === AUTH_PAGE.LOGIN) {
		return formType.value === AUTH_FORM.SIGN_IN ? 'Sign in' : 'Register';
	} else if (step.value === AUTH_PAGE.VERIFICATION) {
		return 'Enter code';
	}
	return 'Sign in';
});

function handleSwitchType() {
	if (formType.value === AUTH_FORM.SIGN_IN) {
		formType.value = AUTH_FORM.SIGN_UP;
	} else if (formType.value === AUTH_FORM.SIGN_UP) {
		formType.value = AUTH_FORM.SIGN_IN;
	}
}

function handleStepChange(currentStep: AUTH_PAGE) {
	step.value = currentStep;
}

function handleGoogleLoginFailed() {
	emit('onGoogleLoginFailed');
}

watch(
	() => authStore.isLoggedIn,
	async (newValue) => {
		emit('onSuccess', newValue);
	},
);
</script>
<template>
  <BaseSideDrawer
    :is-open="isOpen"
    @on-close="emit('onClose')"
  >
    <template #header>
      <div class="authentication-form-header">
        <UserIcon v-if="step === AUTH_PAGE.LOGIN" />
        <ItalicSquareIcon v-if="step === AUTH_PAGE.VERIFICATION" />
        <h1 class="text-xl text-semibold">
          {{ sidebarHeaderMsg }}
        </h1>
      </div>
    </template>
    <template #content>
      <AuthenticationContainerForm
        :type="formType"
        :is-loading="isLoading"
        :redirect-ids="redirectIds"
        :page="page"
        is-sidebar
        @on-switch-type="handleSwitchType"
        @on-step-change="handleStepChange"
        @on-google-login-failed="handleGoogleLoginFailed"
      />
    </template>
    <template #footer />
  </BaseSideDrawer>
</template>
<style scoped lang="scss">
.authentication-form {
  &-header {
    display: flex;
    align-items: center;
    gap: spacings-get(2)
  }

  &-footer {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: spacings-get(4);
    height: 100%;
    padding: 0 spacings-get(6);
    border-top: 1px solid #{colors-get(gray, 200)};

    .btn {
      width: rem(100);
    }
  }
}
</style>